import { ShouldRevalidateFunctionArgs, useRouteLoaderData } from 'react-router';
import type { ActionFunctionArgs } from 'react-router';
import getSupabaseServerClient from '~/core/supabase/server-client.server';
import { deleteTranscript, updateTranscript } from '~/lib/transcript/queries';
import FilesTable from './FilesTable';
import getLogger from '~/core/logger';
import { captureException, ErrorBoundary } from '@sentry/remix';
import {  TranscribeProjectLoader } from '../_app.transcribe_.$id/route';

const logger = getLogger();

export async function action({ request,params }: ActionFunctionArgs) {
  try {
    const client = getSupabaseServerClient(request);
    const transcriptData = await request.formData();
    const transcriptIdList = transcriptData.getAll('transcriptId') as string[];
    const transcriptTitle = transcriptData.get('title') as string;
    const id = params.id!
    const destinationProjectId: string = transcriptData.get(
      'destinationProjectId',
    ) as string;
    const currentProjectId = transcriptData.get('currentProjectId') as string;

    if (!transcriptIdList.length) {
      return;
    }
    await client.from("transcript_project_analysis").update({
      status: "outdated"
    }).eq("project_id", id);

    if (request.method === 'DELETE') {
      const deleteTranscriptRequests = transcriptIdList.map(async (id) =>
        deleteTranscript(client, { id }),
      );
      await Promise.all(deleteTranscriptRequests);
    } else if (
      request.method === 'PUT' &&
      destinationProjectId &&
      currentProjectId
    ) {
      const updateTranscriptRequests = transcriptIdList.map(async (id) =>
        updateTranscript(client, { project_id: destinationProjectId, id }),
      );
      await Promise.all(updateTranscriptRequests);
    } else if (request.method === 'POST' && transcriptTitle) {
      await updateTranscript(client, {
        title: transcriptTitle,
        id: transcriptIdList[0],
      });
    }
  } catch (e) {
    logger.error(e);
    captureException(e);
  }
  return null;
}

export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  if (args.actionStatus) {
    return true;
  }

  const nextTranscriptionIdList =
    args.nextUrl.searchParams.getAll('transcriptionId');
  const currentTranscriptionIdList =
    args.currentUrl.searchParams.getAll('transcriptionId');
  const isCheckedTranscriptionChanged =
    nextTranscriptionIdList.length !== currentTranscriptionIdList.length ||
    !nextTranscriptionIdList.some((id) =>
      currentTranscriptionIdList.includes(id),
    );
  if (isCheckedTranscriptionChanged) {
    return false;
  }
  return args.defaultShouldRevalidate;
}

export default function Project() {
  const data = useRouteLoaderData<TranscribeProjectLoader>(
    'routes/_app.transcribe_.$id',
  );

  return (
    <>
      <ErrorBoundary fallback={<>Error occured during transcriptions render</>}>
        <FilesTable
          isDefault={data?.isDefault}
          transcripts={data?.transcripts || []}
        />
      </ErrorBoundary>
    </>
  );
}
